import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Typography,
  Card,
  Button,
  Table,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Radio,
  Select,
  Tag,
  Popconfirm,
  Tooltip,
} from 'antd';
import { ArrowLeftOutlined, PlusOutlined, CheckCircleOutlined, CloseCircleOutlined, BookOutlined, PictureOutlined, EditOutlined, DeleteOutlined, SettingOutlined } from '@ant-design/icons';
import { getSession, addRound, updateRound, deleteRound, updateSession } from '../../utils/sessionStorage';
import { parseDeckList } from '../../utils/deckList';
import LeaderPicker from './LeaderPicker';
import DeckCardPicker from './DeckCardPicker';
import CardImage from '../CardImage';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const NARROW_BREAKPOINT = 640;
const { Title, Text } = Typography;
const { TextArea } = Input;

function isLeaderCard(card) {
  const cat = (card.category || '').toLowerCase();
  const types = (card.types || []).map((t) => (t || '').toLowerCase());
  return cat.includes('leader') || types.some((t) => t.includes('leader'));
}

export default function SessionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [cards, setCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(true);
  const [roundModalOpen, setRoundModalOpen] = useState(false);
  const [editingRoundId, setEditingRoundId] = useState(null);
  const [editingPlacement, setEditingPlacement] = useState(false);
  const [placementValue, setPlacementValue] = useState(null);
  const [deckModalOpen, setDeckModalOpen] = useState(false);
  const [deckCodeInput, setDeckCodeInput] = useState('');
  const [handModal, setHandModal] = useState(null); // { roundId: string | null, type: 'pre'|'post' } when open
  const [newRoundHands, setNewRoundHands] = useState({ didMulligan: false, openingHandPreMulligan: [], openingHandPostMulligan: [] });
  const [handsViewRound, setHandsViewRound] = useState(null); // round to show in hands popup
  const [isNarrow, setIsNarrow] = useState(typeof window !== 'undefined' && window.innerWidth < NARROW_BREAKPOINT);
  const [sessionEditModalOpen, setSessionEditModalOpen] = useState(false);
  const [sessionOptions, setSessionOptions] = useState({ formats: [], session_types: [] });
  const [form] = Form.useForm();
  const [sessionEditForm] = Form.useForm();
  const sessionEditSessionType = Form.useWatch('sessionType', sessionEditForm);

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < NARROW_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const leaderOptions = useMemo(() => {
    const leaders = cards.filter(isLeaderCard);
    return leaders.length > 0 ? leaders : cards;
  }, [cards]);

  useEffect(() => {
    setSession(getSession(id));
  }, [id]);

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/api/cards/all-base-cards`)
      .then(({ data }) => setCards(Array.isArray(data) ? data : []))
      .catch(() => setCards([]))
      .finally(() => setLoadingCards(false));
  }, []);

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/api/session-options`)
      .then(({ data }) => setSessionOptions({
        formats: data.formats || [],
        session_types: data.session_types || [],
      }))
      .catch(() => setSessionOptions({ formats: [], session_types: [] }));
  }, []);

  const refreshSession = () => setSession(getSession(id));

  const openAddRound = () => {
    setEditingRoundId(null);
    setNewRoundHands({ didMulligan: false, openingHandPreMulligan: [], openingHandPostMulligan: [] });
    form.resetFields();
    setRoundModalOpen(true);
  };

  const openEditRound = (round) => {
    setEditingRoundId(round.id);
    setRoundModalOpen(true);
  };

  // After modal opens for edit, set form values (Form remounts with destroyOnClose so setFieldsValue must run after open)
  useEffect(() => {
    if (!roundModalOpen || !editingRoundId || !session?.rounds) return;
    const round = session.rounds.find((r) => r.id === editingRoundId);
    if (round) {
      form.setFieldsValue({
        enemyLeaderCardId: round.enemyLeaderCardId,
        won: round.won === true,
        wonDieRoll: round.wonDieRoll === true,
        wentFirst: round.wentFirst === true,
        notes: round.notes ?? '',
      });
    }
  }, [roundModalOpen, editingRoundId, session?.rounds, form]);

  const editingRound = editingRoundId ? session?.rounds?.find((r) => r.id === editingRoundId) : null;
  const roundMulligan = editingRound ? editingRound.didMulligan : newRoundHands.didMulligan;
  const setRoundMulligan = (checked) => {
    if (editingRoundId) updateRound(id, editingRoundId, { didMulligan: checked });
    else setNewRoundHands((p) => ({ ...p, didMulligan: checked }));
    refreshSession();
  };
  const openHandPicker = (type) => setHandModal({ roundId: editingRoundId, type });
  const roundHandPreCount = editingRound?.openingHandPreMulligan?.length ?? newRoundHands.openingHandPreMulligan.length;
  const roundHandPostCount = editingRound?.openingHandPostMulligan?.length ?? newRoundHands.openingHandPostMulligan.length;

  const handleRoundSubmit = () => {
    form.validateFields().then((values) => {
      const rawId = values.enemyLeaderCardId;
      const leaderCard = leaderOptions.find(
        (c) => String(c.card_id) === String(rawId) || (c.name && `${c.card_id} ${c.name}` === rawId)
      ) || cards.find(
        (c) => String(c.card_id) === String(rawId) || (c.name && `${c.card_id} ${c.name}` === rawId)
      );
      const enemyLeaderCardId = leaderCard?.card_id ?? rawId;
      const enemyLeaderName = leaderCard?.name ?? (typeof rawId === 'string' && rawId.includes(' ') ? rawId.split(' ').slice(1).join(' ') : null) ?? rawId ?? '';
      const enemyLeaderImageUrl = leaderCard?.img_full_url ?? null;

      if (editingRoundId) {
        updateRound(id, editingRoundId, {
          enemyLeaderCardId,
          enemyLeaderName,
          enemyLeaderImageUrl,
          won: values.won,
          wonDieRoll: values.wonDieRoll,
          wentFirst: values.wentFirst,
          notes: values.notes ?? '',
        });
      } else {
        addRound(id, {
          enemyLeaderCardId,
          enemyLeaderName,
          enemyLeaderImageUrl,
          won: values.won,
          wonDieRoll: values.wonDieRoll,
          wentFirst: values.wentFirst,
          notes: values.notes ?? '',
          didMulligan: newRoundHands.didMulligan,
          openingHandPreMulligan: newRoundHands.openingHandPreMulligan,
          openingHandPostMulligan: newRoundHands.openingHandPostMulligan,
        });
      }
      refreshSession();
      setRoundModalOpen(false);
      form.resetFields();
    });
  };

  const savePlacement = () => {
    const value = placementValue != null && placementValue !== '' ? Number(placementValue) : null;
    updateSession(id, { finalPlacement: value });
    setEditingPlacement(false);
    setPlacementValue(null);
    refreshSession();
  };

  const openSessionEdit = () => {
    sessionEditForm.setFieldsValue({
      tournamentName: session.tournamentName || '',
      sessionType: session.sessionType || 'tournament',
      myLeaderCardId: session.myLeaderCardId ?? undefined,
      format: session.format || undefined,
      playerCount: session.playerCount ?? 32,
      finalPlacement: session.finalPlacement != null && session.finalPlacement !== '' ? session.finalPlacement : undefined,
    });
    setSessionEditModalOpen(true);
  };

  const handleSessionEditSubmit = () => {
    sessionEditForm.validateFields().then((values) => {
      const sessionType = values.sessionType === 'practice' ? 'practice' : 'tournament';
      const leaderCard = leaderOptions.find(
        (c) => c.card_id === values.myLeaderCardId || `${c.card_id} ${c.name}` === values.myLeaderCardId
      );
      const myLeaderCardId = leaderCard?.card_id ?? values.myLeaderCardId;
      const myLeaderName = leaderCard?.name ?? values.myLeaderCardId ?? '';
      const myLeaderImageUrl = leaderCard?.img_full_url ?? null;
      const updates = {
        tournamentName: values.tournamentName?.trim() || 'Unnamed',
        sessionType,
        myLeaderCardId: myLeaderCardId || null,
        myLeaderName,
        myLeaderImageUrl,
        format: values.format || null,
        playerCount: sessionType === 'tournament' ? (values.playerCount ?? 0) : null,
        finalPlacement: sessionType === 'tournament' && values.finalPlacement != null && values.finalPlacement !== '' ? values.finalPlacement : null,
      };
      updateSession(id, updates);
      setSessionEditModalOpen(false);
      refreshSession();
    });
  };

  const handleDeleteRound = (roundId) => {
    deleteRound(id, roundId);
    refreshSession();
  };

  const handleSaveDeck = () => {
    const entries = parseDeckList(deckCodeInput);
    const deckCards = [];
    const seen = new Map();
    for (const { cardNumber, quantity } of entries) {
      const card = cards.find((c) => (c.card_id || '').toUpperCase() === cardNumber);
      const cardId = card?.card_id ?? cardNumber;
      if (seen.has(cardId)) {
        const idx = seen.get(cardId);
        deckCards[idx].quantity += quantity;
      } else {
        seen.set(cardId, deckCards.length);
        deckCards.push({
          card_id: cardId,
          name: card?.name ?? cardNumber,
          img_full_url: card?.img_full_url ?? null,
          quantity,
        });
      }
    }
    updateSession(id, { deckCode: deckCodeInput.trim() || null, deckCards });
    setDeckCodeInput('');
    setDeckModalOpen(false);
    refreshSession();
  };

  const handleHandChange = (cardIds) => {
    if (!handModal) return;
    const ids = cardIds || [];
    if (handModal.roundId) {
      if (handModal.type === 'pre') updateRound(id, handModal.roundId, { openingHandPreMulligan: ids });
      else updateRound(id, handModal.roundId, { openingHandPostMulligan: ids });
    } else {
      if (handModal.type === 'pre') setNewRoundHands((p) => ({ ...p, openingHandPreMulligan: ids }));
      else setNewRoundHands((p) => ({ ...p, openingHandPostMulligan: ids }));
    }
    refreshSession();
  };

  const currentRoundForHand = handModal && handModal.roundId ? session?.rounds?.find((r) => r.id === handModal.roundId) : null;
  const handPickerValue =
    handModal?.type === 'pre'
      ? (currentRoundForHand?.openingHandPreMulligan ?? newRoundHands.openingHandPreMulligan)
      : (currentRoundForHand?.openingHandPostMulligan ?? newRoundHands.openingHandPostMulligan);

  const rounds = session?.rounds || [];
  const getRoundLeaderImageUrl = useCallback((round) => {
    const stored = round.enemyLeaderImageUrl;
    if (stored && String(stored).trim()) return stored;
    const cid = round.enemyLeaderCardId;
    if (cid == null || cid === '') return null;
    const card = leaderOptions.find((c) => String(c.card_id) === String(cid))
      || cards.find((c) => String(c.card_id) === String(cid));
    return (card?.img_full_url && String(card.img_full_url).trim()) ? card.img_full_url : null;
  }, [leaderOptions, cards]);
  const roundsWithLeaderImg = useMemo(
    () => rounds.map((r) => ({ ...r, _enemyLeaderImgUrl: getRoundLeaderImageUrl(r) })),
    [rounds, getRoundLeaderImageUrl]
  );

  if (!session) {
    return (
      <div className="session-tracker-detail">
        <Text type="secondary">Session not found.</Text>
        <Button onClick={() => navigate('/session-tracker')} style={{ marginLeft: 12 }}>
          Back to list
        </Button>
      </div>
    );
  }

  const wins = rounds.filter((r) => r.won).length;
  const isTournament = session.sessionType !== 'practice';
  const roundLabel = isTournament ? 'round' : 'game';
  const roundLabelCap = isTournament ? 'Round' : 'Game';

  const sessionDate = (() => {
    if (!session.createdAt) return null;
    try {
      const d = new Date(session.createdAt);
      return isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return null;
    }
  })();

  const deckCards = session.deckCards || [];
  const getCardImg = (cardId) => deckCards.find((c) => c.card_id === cardId)?.img_full_url;

  const renderHandCards = (cardIds, large = false) => {
    if (!cardIds?.length) return <span className="session-tracker-hand-empty">—</span>;
    const wrapClass = large ? 'session-tracker-hand-cards session-tracker-hand-cards-large' : 'session-tracker-hand-cards';
    const cardClass = large ? 'session-tracker-hand-card session-tracker-hand-card-large' : 'session-tracker-hand-card';
    return (
      <div className={wrapClass}>
        {cardIds.map((cid, i) => {
          const src = getCardImg(cid);
          return (
            <Tooltip key={`${cid}-${i}`} title={deckCards.find((c) => c.card_id === cid)?.name ?? cid}>
              <span className={cardClass}>
                {src ? (
                  <CardImage src={src} alt="" wrapperClassName="session-tracker-hand-card-inner" />
                ) : (
                  <span className="session-tracker-hand-card-placeholder">?</span>
                )}
              </span>
            </Tooltip>
          );
        })}
      </div>
    );
  };

  const roundColumns = [
    { title: '#', key: 'index', width: 48, render: (_, __, i) => i + 1 },
    {
      title: 'Enemy leader',
      key: 'enemyLeader',
      width: 140,
      render: (_, round) => {
        const imgUrl = round._enemyLeaderImgUrl;
        return (
          <div className="session-tracker-round-leader">
            {imgUrl ? (
              <div className="session-tracker-round-leader-img-wrap">
                <img
                  src={imgUrl}
                  alt=""
                  className="session-tracker-round-leader-img"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                />
              </div>
            ) : null}
            <span className="session-tracker-round-leader-name">{round.enemyLeaderName || '—'}</span>
          </div>
        );
      },
    },
    {
      title: 'Result',
      dataIndex: 'won',
      key: 'won',
      width: 88,
      render: (won) =>
        won ? (
          <span className="session-tracker-result session-tracker-result-won">
            <CheckCircleOutlined /> W
          </span>
        ) : (
          <span className="session-tracker-result session-tracker-result-lost">
            <CloseCircleOutlined /> L
          </span>
        ),
    },
    {
      title: (
        <span title="Won die roll">
          <span className="session-tracker-die-roll-header">🎲</span>
        </span>
      ),
      dataIndex: 'wonDieRoll',
      key: 'wonDieRoll',
      width: 56,
      align: 'center',
      render: (v) => (v ? <span className="session-tracker-die-won">🎲</span> : <span className="session-tracker-die-lost">✗</span>),
    },
    {
      title: 'Played',
      dataIndex: 'wentFirst',
      key: 'wentFirst',
      width: 72,
      align: 'center',
      render: (v) => (v ? <span className="session-tracker-played">1st</span> : <span className="session-tracker-played session-tracker-played-2nd">2nd</span>),
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      width: 180,
      render: (notes) => {
        const text = notes && String(notes).trim() ? notes : '—';
        return (
          <Tooltip title={text !== '—' ? text : null}>
            <div className="session-tracker-notes-cell">{text}</div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Hands',
      key: 'hands',
      width: 56,
      align: 'center',
      render: (_, round) => (
        <Button
          type="text"
          size="small"
          icon={<PictureOutlined />}
          onClick={() => setHandsViewRound(round)}
          title="View opening hands"
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, round) => (
        <Space size="small" wrap>
          <Button type="link" size="small" onClick={() => openEditRound(round)}>
            Edit
          </Button>
          <Popconfirm title={`Delete this ${roundLabel}?`} onConfirm={() => handleDeleteRound(round.id)} okText="Delete" cancelText="Cancel">
            <Button type="link" size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="session-tracker-detail">
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/session-tracker')} style={{ marginBottom: 8 }}>
        Back to sessions
      </Button>

      <Card className="session-tracker-detail-header-card">
        <div className="session-tracker-detail-header">
          {session.myLeaderImageUrl && (
            <div className="session-tracker-detail-leader-img-wrap">
              <CardImage
                src={session.myLeaderImageUrl}
                alt={session.myLeaderName}
                wrapperClassName="session-tracker-detail-leader-img-inner"
              />
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <Space align="center" wrap style={{ marginBottom: 4 }}>
              <Title level={5} style={{ marginTop: 0, marginBottom: 0 }}>{session.tournamentName}</Title>
              <Button type="text" size="small" icon={<SettingOutlined />} onClick={openSessionEdit} title="Edit session">
                Edit session
              </Button>
            </Space>
            <Space wrap align="center">
              <Button
                size="small"
                icon={<BookOutlined />}
                onClick={() => { setDeckCodeInput(session.deckCode || ''); setDeckModalOpen(true); }}
              >
                {(session.deckCards || []).length > 0
                  ? `Deck (${(session.deckCards || []).reduce((s, c) => s + (c.quantity || 0), 0)} cards)`
                  : 'Set deck'}
              </Button>
              {sessionDate && (
                <>
                  <Text type="secondary">{sessionDate}</Text>
                  <Text type="secondary">·</Text>
                </>
              )}
              <Text type="secondary">Your leader: {session.myLeaderName || session.myLeaderCardId || '—'}</Text>
              {session.format && (
                <>
                  <Text type="secondary">·</Text>
                  <Text type="secondary">{session.format}</Text>
                </>
              )}
              {isTournament && (
                <>
                  <Text type="secondary">·</Text>
                  <Text type="secondary">{session.playerCount} players</Text>
                  <Text type="secondary">·</Text>
                  {editingPlacement ? (
                    <Space size="small">
                      <InputNumber
                        min={0}
                        size="small"
                        placeholder="Placement"
                        value={placementValue !== null && placementValue !== undefined ? placementValue : session.finalPlacement ?? ''}
                        onChange={(v) => setPlacementValue(v)}
                        style={{ width: 72 }}
                      />
                      <Button size="small" type="primary" onClick={savePlacement}>Save</Button>
                      <Button size="small" onClick={() => { setEditingPlacement(false); setPlacementValue(null); }}>Cancel</Button>
                    </Space>
                  ) : (
                    <>
                      <Text type="secondary">
                        Placement: {session.finalPlacement != null ? session.finalPlacement : '—'}
                      </Text>
                      <Button type="link" size="small" onClick={() => setEditingPlacement(true)}>
                        {session.finalPlacement != null ? 'Edit' : 'Set'}
                      </Button>
                    </>
                  )}
                </>
              )}
              <Text type="secondary">·</Text>
              <Text>{roundLabelCap}s: {wins}W – {rounds.length - wins}L</Text>
            </Space>
          </div>
        </div>
      </Card>

      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openAddRound}>
          Add {roundLabel}
        </Button>
      </Space>

      {isNarrow ? (
        <div className="session-tracker-rounds-mobile">
          {rounds.length === 0 ? (
            <div className="session-tracker-rounds-mobile-empty">No {roundLabel}s yet. Add one above.</div>
          ) : (
            roundsWithLeaderImg.map((round, i) => {
              const imgUrl = round._enemyLeaderImgUrl;
              return (
              <div key={round.id} className="session-tracker-round-card">
                <div className="session-tracker-round-card-main">
                  <span className="session-tracker-round-card-index">{i + 1}</span>
                  <div className="session-tracker-round-leader session-tracker-round-card-leader">
                    {imgUrl ? (
                      <div className="session-tracker-round-leader-img-wrap">
                        <img
                          src={imgUrl}
                          alt=""
                          className="session-tracker-round-leader-img"
                          loading="lazy"
                          referrerPolicy="no-referrer"
                        />
                      </div>
                    ) : null}
                    <span className="session-tracker-round-leader-name">{round.enemyLeaderName || '—'}</span>
                  </div>
                  <div className="session-tracker-round-card-meta">
                    {round.won ? (
                      <span className="session-tracker-result session-tracker-result-won"><CheckCircleOutlined /> W</span>
                    ) : (
                      <span className="session-tracker-result session-tracker-result-lost"><CloseCircleOutlined /> L</span>
                    )}
                    <span className="session-tracker-round-card-meta-item">{round.wonDieRoll ? '🎲' : '✗'}</span>
                    <span className="session-tracker-played session-tracker-round-card-played">{round.wentFirst ? '1st' : '2nd'}</span>
                  </div>
                  {(round.notes && String(round.notes).trim()) ? (
                    <Tooltip title={round.notes}>
                      <div className="session-tracker-round-card-notes">{round.notes}</div>
                    </Tooltip>
                  ) : null}
                </div>
                <div className="session-tracker-round-card-actions">
                  <Button type="text" size="small" icon={<PictureOutlined />} onClick={() => setHandsViewRound(round)} title="Opening hands" />
                  <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEditRound(round)} title="Edit" />
                  <Popconfirm title={`Delete this ${roundLabel}?`} onConfirm={() => handleDeleteRound(round.id)} okText="Delete" cancelText="Cancel">
                    <Button type="text" size="small" danger icon={<DeleteOutlined />} title="Delete" />
                  </Popconfirm>
                </div>
              </div>
              );
            })
          )}
        </div>
      ) : (
        <Table
          dataSource={roundsWithLeaderImg}
          columns={roundColumns}
          rowKey="id"
          pagination={false}
          size="small"
          scroll={{ x: 760 }}
          className="session-tracker-rounds-table"
          locale={{ emptyText: `No ${roundLabel}s yet. Add one above.` }}
        />
      )}

      <Modal
        title="Edit session"
        open={sessionEditModalOpen}
        onOk={handleSessionEditSubmit}
        onCancel={() => setSessionEditModalOpen(false)}
        okText="Save"
        width={420}
        destroyOnClose
      >
        <Form form={sessionEditForm} layout="vertical" initialValues={{ sessionType: 'tournament' }}>
          <Form.Item name="tournamentName" label="Session name" rules={[{ required: true, message: 'Enter a name' }]}>
            <Input placeholder="Session name" />
          </Form.Item>
          <Form.Item name="sessionType" label="Session type" rules={[{ required: true }]}>
            <Radio.Group>
              {sessionOptions.session_types.map((t) => (
                <Radio key={t.value} value={t.value}>{t.label}</Radio>
              ))}
            </Radio.Group>
          </Form.Item>
          <Form.Item name="myLeaderCardId" label="Your leader" rules={[{ required: true, message: 'Choose your leader' }]}>
            <LeaderPicker
              options={leaderOptions}
              loading={loadingCards}
              placeholder="Choose your leader"
              label={null}
            />
          </Form.Item>
          <Form.Item name="format" label="Format">
            <Select
              allowClear
              placeholder="Select format"
              options={sessionOptions.formats.map((f) => ({ value: f, label: f }))}
            />
          </Form.Item>
          {sessionEditSessionType === 'tournament' && (
            <>
              <Form.Item name="playerCount" label="Number of players" rules={[{ required: true }]}>
                <InputNumber min={2} max={1000} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="finalPlacement" label="Your final placement (optional)">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="Placement" />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      <Modal
        title="Opening hands"
        open={!!handsViewRound}
        onCancel={() => setHandsViewRound(null)}
        footer={[
          <Button key="close" onClick={() => setHandsViewRound(null)}>Close</Button>,
        ]}
        width={360}
      >
        {handsViewRound && (
          <div className="session-tracker-hands-modal">
            <div className="session-tracker-hands-modal-section">
              <Text strong>Pre-mulligan</Text>
              {renderHandCards(handsViewRound.openingHandPreMulligan, true)}
            </div>
            <div className="session-tracker-hands-modal-section">
              <Text strong>Post-mulligan</Text>
              {renderHandCards(handsViewRound.openingHandPostMulligan, true)}
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title={editingRoundId ? `Edit ${roundLabel}` : `Add ${roundLabel}`}
        open={roundModalOpen}
        onOk={handleRoundSubmit}
        onCancel={() => setRoundModalOpen(false)}
        okText={editingRoundId ? 'Save' : `Add ${roundLabel}`}
        width={420}
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={{ won: true, wonDieRoll: false, wentFirst: true }}>
          <Form.Item name="enemyLeaderCardId" label="Enemy leader" rules={[{ required: true, message: 'Choose enemy leader' }]}>
            <LeaderPicker
              options={leaderOptions}
              loading={loadingCards}
              placeholder="Choose enemy leader"
              label={null}
            />
          </Form.Item>
          <Form.Item name="won" label="Result" rules={[{ required: true, message: 'Select win or lose' }]}>
            <Radio.Group>
              <Radio value={true}>Won</Radio>
              <Radio value={false}>Lost</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item name="wonDieRoll" label="Won die roll" valuePropName="checked">
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>
          <Form.Item name="wentFirst" label="Played">
            <Radio.Group>
              <Radio value={true}>1st</Radio>
              <Radio value={false}>2nd</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <TextArea rows={3} placeholder="Optional notes…" />
          </Form.Item>
          <Form.Item label="Did mulligan?">
            <Switch checked={roundMulligan === true} onChange={setRoundMulligan} />
          </Form.Item>
          {(session.deckCards || []).length > 0 && (
            <>
              <Form.Item label="Opening hand (pre-mulligan)">
                <Button size="small" onClick={() => openHandPicker('pre')}>
                  {roundHandPreCount ? `${roundHandPreCount} cards` : 'Set'}
                </Button>
              </Form.Item>
              <Form.Item label="Opening hand (post-mulligan)">
                <Button size="small" onClick={() => openHandPicker('post')}>
                  {roundHandPostCount ? `${roundHandPostCount} cards` : 'Set'}
                </Button>
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      <Modal
        title="Paste deck list"
        open={deckModalOpen}
        onCancel={() => { setDeckModalOpen(false); setDeckCodeInput(''); }}
        onOk={handleSaveDeck}
        okText="Save deck"
        okButtonProps={{ disabled: !deckCodeInput.trim() }}
        width={480}
        destroyOnClose
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Format: 4xOP13-016, 1xOP13-002 (one per line or comma-separated)
        </Text>
        <TextArea
          rows={6}
          placeholder="4xOP13-016&#10;1xOP13-002&#10;..."
          value={deckCodeInput}
          onChange={(e) => setDeckCodeInput(e.target.value)}
          style={{ fontFamily: 'ui-monospace, monospace' }}
        />
      </Modal>

      <Modal
        title={handModal?.type === 'pre' ? 'Opening hand (pre-mulligan)' : 'Opening hand (post-mulligan)'}
        open={!!handModal}
        onCancel={() => setHandModal(null)}
        footer={null}
        width={560}
        destroyOnClose
      >
        <DeckCardPicker
          deckCards={session.deckCards || []}
          maxSelection={5}
          value={handPickerValue || []}
          onChange={handleHandChange}
        />
        <div style={{ marginTop: 16 }}>
          <Button type="primary" onClick={() => setHandModal(null)}>Done</Button>
        </div>
      </Modal>
    </div>
  );
}
